import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
} from "@mui/material";
import { IoCheckmarkCircleSharp } from "react-icons/io5";

// 🔧 Reusable dynamic table component
const DynamicTable = ({ title, rows, textColor }) => {
  if (!rows || rows.length === 0) return null;

  const columns = Object.keys(rows[0]);

  return (
    <>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Table size="small" sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            {columns.map((col, index) => (
              <TableCell
                key={index}
                sx={{ fontWeight: "bold", textTransform: "capitalize" }}
              >
                <Typography variant="subtitle2" sx={{ fontSize: "14px" }}>
                  {col.replace(/_/g, " ")}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow key={idx}>
              {columns.map((col, i) => (
                <TableCell key={i}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "14px",
                      color: col === "reason" ? textColor : "black",
                    }}
                  >
                    {typeof row[col] === "string"
                      ? row[col]
                          .replace(/_/g, " ")
                          .replace(/^([a-z])/, (_, c) => c.toUpperCase())
                      : (row[col] ?? "N/A")}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

const UploadedTable = ({ label, uploaded, total }) => {
  if (!uploaded || uploaded.length === 0) return null;
  console.log(uploaded, label, total, "huhfrehufehir");

  return (
    <>
      <Typography variant="h6" gutterBottom>
        {label}
      </Typography>
      <Typography variant="subtitle2" gutterBottom>
        Total Uploaded: {total}
      </Typography>
      <Table size="small" sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            {Object.keys(uploaded[0]).map((key, i) => (
              <TableCell
                key={i}
                sx={{ fontWeight: "bold", textTransform: "capitalize" }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontSize: "14px", color: "black" }}
                >
                  {key.replace(/_/g, " ")}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {uploaded.map((row, idx) => (
            <TableRow key={idx}>
              {Object.values(row).map((val, i) => (
                <TableCell key={i}>
                  <Typography
                    variant="body1"
                    sx={{ fontSize: "14px", color: "black" }}
                  >
                    {typeof val === "string"
                      ? val.replace(/^([a-z])/, (_, c) => c.toUpperCase())
                      : val}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

// 🧾 Modal Component
const InvalidRowsModal = ({ open, onClose, data, name }) => {
  const {
    duplicates = [],
    invalid_rows = [],
    failed_rows = [],
    not_found = [],
    errors = [],
    managerReviewerIssue_rows = [],
    total_uploaded,
    uploaded,
    total_updated,
    uploaded_files = [],
    total_inserted,
    inserted_ids = [],
    updated_employee_codes = [],

    failed_files = [],
    inserted_row_numbers = [],
    inserted_employee_ids = [],
    department = {},
    sub_department = {},
    designation = {},
    kra_kpi = {},
  } = data || {};

  const hasErrors =
    duplicates.length > 0 ||
    invalid_rows.length > 0 ||
    failed_rows.length > 0 ||
    not_found.length > 0 ||
    errors.length > 0 ||
    managerReviewerIssue_rows.length > 0 ||
    failed_files.length > 0;

  const formattedErrors = errors.map((error, idx) => ({
    row: idx + 1,
    reason: error,
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        zIndex: 1800,
        "& .MuiPaper-root": {
          maxWidth: hasErrors ? "900px" : "600px",
        },
      }}
    >
      <DialogTitle>Upload Result Summary</DialogTitle>
      <DialogContent dividers>
        {!hasErrors ? (
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <IoCheckmarkCircleSharp
              style={{
                fontSize: "40px",
                color: "#34c38f",
                marginRight: "10px",
              }}
            />
            <Typography variant="subtitle2" sx={{ fontSize: "16px" }}>
              All rows inserted successfully.
            </Typography>
          </Box>
        ) : null}

        {/* ✅ Uploaded Tables */}
        {name === "department" && (
          <UploadedTable
            label="Uploaded Departments"
            uploaded={uploaded}
            total={total_uploaded}
          />
        )}
        {name === "subDepartment" && (
          <UploadedTable
            label="Uploaded Sub-Departments"
            uploaded={uploaded}
            total={total_uploaded}
          />
        )}
        {name === "designation" && (
          <UploadedTable
            label="Uploaded Designations"
            uploaded={uploaded}
            total={total_uploaded}
          />
        )}
        {/* {name === "updateAppraisalPdfUpload" && <UploadedTable label="Uploaded Appraisal PDF's" uploaded={uploaded_files} total={total_uploaded} />} */}

        {name === "updateAppraisalPdfUpload" && uploaded_files?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Total Uploaded: {total_uploaded}
            </Typography>
            <Table size="small" sx={{ mb: 3 }}>
              <TableHead>
                <TableRow>
                  {["S.No", "Appraisal PDF's Name"].map((key, i) => (
                    <TableCell
                      key={i}
                      sx={{ fontWeight: "bold", textTransform: "capitalize" }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontSize: "14px", color: "black" }}
                      >
                        {key}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {uploaded_files.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Typography
                        variant="body1"
                        sx={{ fontSize: "14px", color: "black" }}
                      >
                        {idx + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body1"
                        sx={{ fontSize: "14px", color: "black" }}
                      >
                        {row}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {/* ✅ Inserted Rows Info */}
        {total_inserted > 0 ||
          (total_updated && (
            <>
              {name === "employee" && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {total_updated ? "Total Updated" : "Total Inserted"}:{" "}
                    {total_updated ?? total_inserted}
                  </Typography>

                  {/* <Typography variant="subtitle1">Inserted Row Numbers: {inserted_row_numbers.join(', ')}</Typography> */}
                  {updated_employee_codes > 0 && (
                    <Typography variant="subtitle1">
                      Inserted Employee IDs: {updated_employee_codes.join(", ")}
                    </Typography>
                  )}
                </Box>
              )}
              {name === "krakapimanagement" && (
                <>
                  {inserted_ids > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Total Inserted: {total_inserted}
                      </Typography>

                      {/* <Typography variant="subtitle1">Inserted Ids: {inserted_ids?.join(', ')}</Typography> */}
                    </Box>
                  )}
                </>
              )}
            </>
          ))}

        {name === "updateManagerReviewer" && (
          <>
            {data?.updated_ids > 0 && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Total updated: {data?.total_updated}
                  </Typography>
                  <Typography variant="subtitle1">
                    Inserted Ids: {data?.updated_ids?.join(", ")}
                  </Typography>
                  <Typography variant="subtitle1">
                    Inserted Ids: {data?.updated_row_numbers?.join(", ")}
                  </Typography>
                </Box>
              </>
            )}
          </>
        )}

        {/* ❌ Error Tables */}

        {duplicates.length > 0 && (
          <DynamicTable
            title="Duplicate Rows"
            rows={duplicates}
            textColor="red"
          />
        )}
        {invalid_rows.length > 0 && (
          <DynamicTable
            title="Invalid Rows"
            rows={invalid_rows}
            textColor="red"
          />
        )}
        {failed_rows.length > 0 && (
          <DynamicTable
            title="Failed Rows"
            rows={failed_rows}
            textColor="red"
          />
        )}
        {not_found.length > 0 && (
          <DynamicTable
            title="Not Found Rows"
            rows={not_found}
            textColor="red"
          />
        )}
        {formattedErrors.length > 0 && (
          <DynamicTable title="Errors" rows={formattedErrors} textColor="red" />
        )}
        {managerReviewerIssue_rows.length > 0 && (
          <DynamicTable
            title="Reviewer/Manager Issue Rows"
            rows={managerReviewerIssue_rows}
            textColor="red"
          />
        )}
        {failed_files.length > 0 && (
          <DynamicTable
            title="Failed Files"
            rows={failed_files}
            textColor="red"
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            background: !hasErrors ? "#34c38f" : "var(--main-color)",
          }}
        >
          {!hasErrors ? "Ok" : "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvalidRowsModal;
